import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { influencerCampaignAPIClient } from "@/marketing/infrastructure/influencer/campaign/influencerCampaignAPIClient";
import { campaignPipelineAPIClient } from "@/marketing/infrastructure/influencer/campaign/pipeline/campaignPipelineAPIClient";
import { CAMPAIGN_PIPELINE_KEYS } from "@/marketing/infrastructure/influencer/campaign/pipeline/utils/keys";
import { INFLUENCER_CAMPAIGN_KEYS } from "@/marketing/infrastructure/influencer/campaign/utils/keys";
import { extractApiErrorMessage } from "@/marketing/lib/extractApiErrorMessage";
import type { CampaignOperationsBatches, CampaignOperationsExpectedValues } from "@/marketing/models/campaignOperations";
import { useAuthStore } from "@/marketing/stores/useAuthStore";
import { CampaignOperationsConflicts } from "@/marketing/components/influencerCampaign/tracker/campaignOperationsConflicts";

export interface CampaignBatchActions {
  changeBatches: (batches: CampaignOperationsBatches, expected: CampaignOperationsExpectedValues) => Promise<void>;
  // Resolves true once the creators moved, false after a failure the toast already reported.
  moveToBatch: (influencerIds: string[], batchId: string | null, batchName: string) => Promise<boolean>;
}

// Batch conflicts name the field they hit; a whole-batch conflict (add or delete) reads as the batch itself.
const BATCH_FIELD_LABELS: Record<string, string> = {
  name: "this batch's name",
  "phases.shipping.dueDate": "this batch's shipping due date",
  "phases.posting.dueDate": "this batch's posting due date",
};

const batchFieldLabel = (field: string) => BATCH_FIELD_LABELS[field.split(".").slice(2).join(".")] ?? "this batch";

/** Saves batch edits and assignments for one campaign; failures become toasts, so callers never need to catch. */
export function useCampaignBatchActions(campaignId: string): CampaignBatchActions {
  const queryClient = useQueryClient();
  const editorEmail = useAuthStore((state) => state.user?.email);

  // Deleting a batch unassigns its creators on the server, so the creator list reloads too.
  const reload = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: INFLUENCER_CAMPAIGN_KEYS.detail(campaignId) }),
      queryClient.invalidateQueries({ queryKey: INFLUENCER_CAMPAIGN_KEYS.lists() }),
      queryClient.invalidateQueries({ queryKey: CAMPAIGN_PIPELINE_KEYS.lists() }),
    ]);

  const changeBatches = async (batches: CampaignOperationsBatches, expected: CampaignOperationsExpectedValues) => {
    try {
      await influencerCampaignAPIClient.updateInfluencerCampaign(campaignId, {
        operations: { batches },
        operationsExpected: expected,
        ...(editorEmail ? { operationsEditor: editorEmail } : {}),
      });
    } catch (error) {
      const [conflict] = CampaignOperationsConflicts.from(error);
      toast.error(
        conflict
          ? `Someone else changed ${batchFieldLabel(conflict.field)} to ${CampaignOperationsConflicts.valueText(conflict.current)}. Showing their value.`
          : extractApiErrorMessage(error, "Could not save the batch"),
      );
    } finally {
      await reload();
    }
  };

  const moveToBatch = async (influencerIds: string[], batchId: string | null, batchName: string) => {
    try {
      const { updated } = await campaignPipelineAPIClient.setCampaignInfluencersBatch(campaignId, { influencerIds, batchId });
      toast.success(`Moved ${updated} creator${updated === 1 ? "" : "s"} to ${batchName}`);
      return true;
    } catch (error) {
      toast.error(extractApiErrorMessage(error, "Could not move the creators"));
      return false;
    } finally {
      await queryClient.invalidateQueries({ queryKey: CAMPAIGN_PIPELINE_KEYS.lists() });
    }
  };

  return { changeBatches, moveToBatch };
}
